import {Moment} from 'moment-timezone'

import {OptimizedSchedule, Schedule} from '../../../interfaces/api'
import {usePeriodicAutofillCheck} from '../../usePeriodicAutofillCheck'

import {useCurrentScheduleQuery} from './useCurrentScheduleQuery'
import {useOptimizedScheduleQuery} from './useOptimizedScheduleQuery'

interface UseScheduleQueryParams {
  // pass scheduleId for optimized schedule
  scheduleId?: number | undefined
  throwOnError?: boolean
  range: [Moment, Moment]
}

interface UseOptimizedScheduleQueryResult {
  data: OptimizedSchedule | undefined
  isOptimized: true
  isFetching: boolean
}
interface UseCurrentScheduleQueryResult {
  data: Schedule | undefined
  isOptimized: false
  isFetching: boolean
}

type UseScheduleQueryResult<T extends number | undefined> = T extends number
  ? UseOptimizedScheduleQueryResult
  : UseCurrentScheduleQueryResult

export const useScheduleQuery = <T extends number | undefined>({
  scheduleId,
  throwOnError = true,
  range
}: UseScheduleQueryParams): UseScheduleQueryResult<T> => {
  // schedule is reloaded if there is a change in the autofill status
  usePeriodicAutofillCheck()

  const {data: currentSchedule, isFetching: isCurrentScheduleFetching} = useCurrentScheduleQuery({
    range
  })

  const {data: optimizedSchedule, isFetching: isOptimizedScheduleFetching} =
    useOptimizedScheduleQuery({
      scheduleId,
      isDisabled: scheduleId === undefined,
      throwOnError,
      range
    })

  return {
    isOptimized: scheduleId !== undefined,
    data: scheduleId !== undefined ? optimizedSchedule : currentSchedule,
    isFetching: isCurrentScheduleFetching || isOptimizedScheduleFetching
  } as UseScheduleQueryResult<T>
}
